import React, { useEffect, useRef, useState } from "react";
import useToast from "../hooks/useToast";
import { useDispatch, useSelector } from "react-redux";
import useGoTo from "../hooks/useGoTo";
import { useParams } from "react-router";
import { resetSelfcheckoutSession } from "../ducks/selfcheckoutSession";
import Decimal from "decimal.js";
import toPriceDisplay from "../utils/toPriceDisplay";
import TotemResult from "../containers/Selfcheckout/TotemResult";
import { useHoldRest } from "../hooks/useResting";

const SECONDS_RESET = 60;

const PaymentSuccess = ({ shortReference, handleNew }) => {
  const [seconds, setSeconds] = React.useState(30);

  useEffect(() => {
    const id = setInterval(() => setSeconds((v) => v - 1), 1000);
    return () => clearInterval(id);
  }, []);

  const jaReiniciou = useRef(false);
  // Antes isto rodava no corpo do render (`if (seconds <= 0) handleNew()`),
  // ou seja, um history.replace durante a renderização — e repetindo a cada
  // tick depois do zero. O ref garante que dispare uma vez só.
  useEffect(() => {
    if (seconds > 0 || jaReiniciou.current) return;
    jaReiniciou.current = true;
    handleNew();
  }, [seconds, handleNew]);

  return (
    <TotemResult
      tone="success"
      title="Pagamento aprovado!"
      message="Vamos preparar o seu pedido e chamar pelo número quando estiver pronto."
      orderNumber={shortReference}
      actions={
        <button
          type="button"
          className="btn btn-shop totem-btn totem-btn--primary"
          onClick={handleNew}
        >
          Novo pedido ({seconds})
        </button>
      }
    />
  );
};

const PaymentError = ({
  shortReference,
  paymentError,
  handleTryAgain,
  handleCancel,
}) => {
  const [seconds, setSeconds] = React.useState(SECONDS_RESET);

  useEffect(() => {
    const id = setInterval(() => setSeconds((v) => v - 1), 1000);
    return () => clearInterval(id);
  }, []);

  const jaCancelou = useRef(false);
  // Antes isto rodava no corpo do render (`if (seconds <= 0) handleCancel()`),
  // ou seja, um history.replace durante a renderização — e repetindo a cada
  // tick depois do zero. O ref garante que dispare uma vez só.
  useEffect(() => {
    if (seconds > 0 || jaCancelou.current) return;
    jaCancelou.current = true;
    handleCancel();
  }, [seconds, handleCancel]);

  return (
    <TotemResult
      tone="danger"
      title="O pagamento não foi concluído"
      message={paymentError}
      orderNumber={shortReference}
      actions={
        <>
          <button
            type="button"
            className="totem-btn totem-btn--secondary"
            onClick={handleCancel}
          >
            Cancelar pedido {seconds < 30 ? `(${seconds})` : ""}
          </button>
          <button
            type="button"
            className="btn btn-shop totem-btn totem-btn--primary"
            onClick={handleTryAgain}
          >
            Tentar novamente
          </button>
        </>
      }
    />
  );
};

const PaymentForbiden = ({ handleCancel }) => {
  return (
    <TotemResult
      tone="danger"
      title="Ops..."
      message="Parece que você está no lugar errado."
      actions={
        <button
          type="button"
          className="totem-btn totem-btn--secondary"
          onClick={handleCancel}
        >
          Voltar ao início
        </button>
      }
    />
  );
};

const PaymentOptions = ({
  selfcheckoutConfig,
  total,
  handlePaymentSelect,
  handleCancel,
}) => {
  const [seconds, setSeconds] = React.useState(SECONDS_RESET);

  useEffect(() => {
    const id = setInterval(() => setSeconds((v) => v - 1), 1000);
    return () => clearInterval(id);
  }, []);

  const jaCancelou = useRef(false);
  // Antes isto rodava no corpo do render (`if (seconds <= 0) handleCancel()`),
  // ou seja, um history.replace durante a renderização — e repetindo a cada
  // tick depois do zero. O ref garante que dispare uma vez só.
  useEffect(() => {
    if (seconds > 0 || jaCancelou.current) return;
    jaCancelou.current = true;
    handleCancel();
  }, [seconds, handleCancel]);

  const paymentOptions = [
    {
      id: "pix",
      name: "PIX",
      enable: selfcheckoutConfig.pix,
      icon: (
        <svg width="80" height="80" viewBox="0 0 512 512" fill="currentColor">
          <path d="M242.4 292.5C247.8 287.1 257.1 287.1 262.5 292.5L339.5 369.5C353.7 383.7 372.6 391.5 392.6 391.5H407.7L310.6 488.6C280.3 518.1 231.1 518.1 200.8 488.6L103.3 391.5H112.6C132.6 391.5 151.5 383.7 165.7 369.5L242.4 292.5zM262.5 218.9C257.1 224.3 247.8 224.3 242.4 218.9L165.7 142.1C151.5 127.9 132.6 120.1 112.6 120.1H103.3L200.7 23.37C231.1-5.801 280.3-5.801 310.6 23.37L407.7 120.1H392.6C372.6 120.1 353.7 127.9 339.5 142.1L262.5 218.9zM112.6 142.1C126.4 142.1 139.1 148.3 149.7 158.1L226.4 236.1C238.9 248.6 254.5 254.1 270.5 254.1C286.5 254.1 302.1 248.6 314.6 236.1L391.3 158.1C401.9 148.3 415.6 142.1 429.4 142.1H464C490.5 142.1 512 163.5 512 190.1V322.3C512 348.8 490.5 370.3 464 370.3H429.4C415.6 370.3 401.9 364.1 391.3 353.3L314.6 275.3C302.1 262.8 286.5 257.3 270.5 257.3C254.5 257.3 238.9 262.8 226.4 275.3L149.7 353.3C139.1 364.1 126.4 370.3 112.6 370.3H48C21.49 370.3 0 348.8 0 322.3V190.1C0 163.5 21.49 142.1 48 142.1H112.6z" />
        </svg>
      ),
    },
    {
      id: "debit",
      name: "Débito",
      enable: selfcheckoutConfig.debit,
      icon: (
        <svg width="80" height="80" viewBox="0 0 576 512" fill="currentColor">
          <path d="M512 32C547.3 32 576 60.65 576 96V128H0V96C0 60.65 28.65 32 64 32H512zM576 416C576 451.3 547.3 480 512 480H64C28.65 480 0 451.3 0 416V224H576V416zM112 352C103.2 352 96 359.2 96 368C96 376.8 103.2 384 112 384H176C184.8 384 192 376.8 192 368C192 359.2 184.8 352 176 352H112zM240 384H368C376.8 384 384 376.8 384 368C384 359.2 376.8 352 368 352H240C231.2 352 224 359.2 224 368C224 376.8 231.2 384 240 384z" />
        </svg>
      ),
    },
    {
      id: "credit",
      name: "Crédito",
      enable: selfcheckoutConfig.credit,
      icon: (
        <svg width="80" height="80" viewBox="0 0 576 512" fill="currentColor">
          <path d="M512 32C547.3 32 576 60.65 576 96V128H0V96C0 60.65 28.65 32 64 32H512zM576 416C576 451.3 547.3 480 512 480H64C28.65 480 0 451.3 0 416V224H576V416zM112 352C103.2 352 96 359.2 96 368C96 376.8 103.2 384 112 384H176C184.8 384 192 376.8 192 368C192 359.2 184.8 352 176 352H112zM240 384H368C376.8 384 384 376.8 384 368C384 359.2 376.8 352 368 352H240C231.2 352 224 359.2 224 368C224 376.8 231.2 384 240 384z" />
        </svg>
      ),
    },
    {
      id: "voucher",
      name: "Vale Alimentação",
      enable: selfcheckoutConfig.voucher,
      icon: (
        <svg width="80" height="80" viewBox="0 0 576 512" fill="currentColor">
          <path d="M512 32C547.3 32 576 60.65 576 96V128H0V96C0 60.65 28.65 32 64 32H512zM576 416C576 451.3 547.3 480 512 480H64C28.65 480 0 451.3 0 416V224H576V416zM112 352C103.2 352 96 359.2 96 368C96 376.8 103.2 384 112 384H176C184.8 384 192 376.8 192 368C192 359.2 184.8 352 176 352H112zM240 384H368C376.8 384 384 376.8 384 368C384 359.2 376.8 352 368 352H240C231.2 352 224 359.2 224 368C224 376.8 231.2 384 240 384z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="totem-payment">
      <h1 className="totem-payment__title">Como deseja pagar?</h1>
      {total && (
        <p className="totem-payment__total">
          Total <strong>{toPriceDisplay(total)}</strong>
        </p>
      )}
      <ul className="totem-payment__options">
        {paymentOptions
          .filter((p) => p.enable)
          .map((option) => (
            <li key={option.id} className="totem-payment__cell">
              <button
                type="button"
                className="totem-payment__option"
                onClick={() => handlePaymentSelect(option.id)}
              >
                <span className={`payment-icon-${option.id}`}>
                  {option.icon}
                </span>
                <span className="totem-payment__name">{option.name}</span>
              </button>
            </li>
          ))}
      </ul>
      <button
        type="button"
        className="totem-btn totem-btn--secondary totem-payment__cancel"
        onClick={handleCancel}
      >
        Cancelar pedido {seconds < 30 ? `(${seconds})` : ""}
      </button>
    </div>
  );
};

const PaymentTEFScreen = () => {
  const dispatch = useDispatch();
  const goto = useGoTo();
  const { toastError } = useToast();
  const params = useParams();
  const [paymentSelect, setPaymentSelect] = useState(true);
  const [paymentError, setPaymentError] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const lastCache = useSelector((state) => state.checkout.lastCheckoutCache);
  const shortReference = parseInt(params.shortReference, 10);
  const selfcheckoutConfigs = useSelector(
    (state) => state.shop.selfcheckoutConfigs,
  );
  const selfcheckoutConfig =
    selfcheckoutConfigs?.length > 0 ? selfcheckoutConfigs[0] : {};

  // Enquanto a maquininha processa, ninguém toca na tela: sem isto, um
  // pagamento de mais de 3 minutos caía no descanso, que desmonta esta tela
  // e leva junto os callbacks do TEF.
  const isProcessing = !paymentSelect && !paymentError && !paymentSuccess;
  useHoldRest(isProcessing);

  const handlePaymentSelect = (payment) => {
    if (!window.posNativeInterface) {
      toastError("Interface do TEF nao existe!");
      return;
    }

    const {
      orderId,
      costs: { total },
    } = lastCache;

    const totalCents = new Decimal(total).times(100).toString();
    console.log(totalCents);

    window.posNativeInterface.startTEFPayment(orderId, payment, totalCents);
    setPaymentSelect(false);
  };

  const handleTryAgain = () => {
    setPaymentSelect(true);
    setPaymentError(false);
  };

  const handleNovoPedido = () => {
    dispatch(resetSelfcheckoutSession());
    goto("/");
  };

  const onPaymentSuccess = () => {
    setPaymentSuccess(true);
  };

  const onPaymentFaild = (message) => {
    setPaymentError(message);
  };

  // Os handlers ficam num ref para que o registro na ponte nativa rode uma vez
  // só, sem depender da identidade das funções a cada render.
  const tefHandlersRef = useRef();
  // Sem lista de dependências: roda depois de cada render, mantendo o ref em
  // dia sem escrever nele em fase de render.
  useEffect(() => {
    tefHandlersRef.current = { onPaymentSuccess, onPaymentFaild };
  });

  useEffect(() => {
    // Antes estas duas atribuições ficavam no corpo do componente: refaziam a
    // cada render e nunca eram desfeitas. Se o TEF respondesse depois de o
    // usuário sair da tela, caía num setState de componente desmontado.
    window.tef_onPaymentSuccess = () =>
      tefHandlersRef.current.onPaymentSuccess();
    window.tef_onPaymentFaild = (message) =>
      tefHandlersRef.current.onPaymentFaild(message);

    return () => {
      delete window.tef_onPaymentSuccess;
      delete window.tef_onPaymentFaild;
    };
  }, []);

  if (
    !lastCache ||
    !shortReference ||
    lastCache.shortReference !== shortReference
  ) {
    return <PaymentForbiden handleCancel={handleNovoPedido} />;
  }

  if (paymentError) {
    return (
      <PaymentError
        shortReference={lastCache.shortReference}
        paymentError={paymentError}
        handleTryAgain={handleTryAgain}
        handleCancel={handleNovoPedido}
      />
    );
  }

  if (paymentSuccess) {
    return (
      <PaymentSuccess
        shortReference={lastCache.shortReference}
        handleNew={handleNovoPedido}
      />
    );
  }

  if (paymentSelect) {
    return (
      <PaymentOptions
        total={lastCache.costs?.total}
        handlePaymentSelect={handlePaymentSelect}
        handleCancel={handleNovoPedido}
        selfcheckoutConfig={selfcheckoutConfig}
      />
    );
  }

  return (
    <TotemResult
      tone="pending"
      title="Processando o pagamento"
      message="Siga as instruções na maquininha."
    />
  );
};

export default PaymentTEFScreen;
